import React from 'react';
import { Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import LinkOff from '@mui/icons-material/LinkOff';
import Logout from '@mui/icons-material/Logout';
import Person4 from '@mui/icons-material/Person4';
import { usePortalAuthContext } from '../../contexts/portalAuth';
import { LegalLinks } from '../ui/legalLinks/legalLinks.component';
import { PortalLogin } from './portalLogin.component';
import { PortalNav } from './portalNav.component';
import { PortalThemeToggle } from './portalThemeToggle.component';
import styles from './portal.module.css';

export const PortalLayout = () => {
  const { t } = useTranslation();
  const { portal, contact, isSignedIn, signOut } = usePortalAuthContext();

  if (portal === undefined || (portal && contact === undefined)) {
    return (
      <Box className={styles.page}>
        <Box className={styles.centered}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  // Either no tenant owns this slug, or its portal is switched off. The two
  // are deliberately indistinguishable from out here.
  if (portal === null) {
    return (
      <Box className={styles.page}>
        <Box className={styles.centered}>
          <Box className={styles.placeholder}>
            <LinkOff sx={{ fontSize: 48, color: 'text.disabled' }} />
            <Typography variant="body1" color="text.secondary">
              {t('This service portal is not available.')}
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box className={styles.page}>
      <Box component="header" className={styles.header}>
        <Box className={styles.brand}>
          {portal.logo ? (
            <img className={styles.logo} src={portal.logo} alt={portal.name} />
          ) : null}
          <Typography variant="h6" noWrap>
            {portal.name}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {isSignedIn ? (
            <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 1 }}>
              <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                <Person4 fontSize="small" />
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }} noWrap>
                  {contact?.client_name}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                  {contact?.email}
                </Typography>
              </Box>
            </Box>
          ) : null}
          <PortalThemeToggle />
          {isSignedIn ? (
            <Tooltip title={t('Log out')}>
              <IconButton size="small" onClick={signOut} aria-label={t('Log out')}>
                <Logout fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : null}
        </Box>
      </Box>

      {isSignedIn ? (
        <>
          <PortalNav />
          <Box className={styles.content}>
            <Outlet />
          </Box>
        </>
      ) : (
        <PortalLogin />
      )}

      {/* The portal is the one place someone from outside the workspace signs
          in, so the notice telling them who controls their data belongs on
          every screen - a magic link skips the login form entirely. Renders
          nothing until that document is published. */}
      <Box
        component="footer"
        sx={{ display: 'flex', justifyContent: 'center', px: 2, py: 2 }}
      >
        <LegalLinks kinds={['PUN']} />
      </Box>
    </Box>
  );
};
